import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Clock,
  Check,
  X,
  Mail,
  Phone,
  User,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";

interface PendingInvitation {
  id: string;
  invitee_name: string;
  invitee_email: string;
  invitee_phone: string | null;
  status: string;
  created_at: string;
  invited_by: string;
  inviter_email?: string;
}

interface PendingInvitationsCardProps {
  teamId: string;
  onUpdate: () => void;
}

const PendingInvitationsCard: React.FC<PendingInvitationsCardProps> = ({
  teamId,
  onUpdate,
}) => {
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchInvitations = async () => {
    try {
      const { data, error } = await supabase
        .from("team_member_invitations")
        .select("*")
        .eq("team_id", teamId)
        .eq("status", "pending_approval")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch inviter emails
      if (data && data.length > 0) {
        const inviterIds = [...new Set(data.map((i) => i.invited_by))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, email")
          .in("id", inviterIds);

        const profileMap = new Map(
          profiles?.map((p) => [p.id, p.email || "Unknown"]) || []
        );

        const enriched = data.map((inv) => ({
          ...inv,
          inviter_email: profileMap.get(inv.invited_by) || "Unknown",
        }));

        setInvitations(enriched);
      } else {
        setInvitations([]);
      }
    } catch (err) {
      console.error("Failed to fetch pending invitations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [teamId]);

  const handleApprove = async (invitation: PendingInvitation) => {
    setProcessingId(invitation.id);

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");

      // Update invitation status
      const { error: updateError } = await supabase
        .from("team_member_invitations")
        .update({
          status: "pending",
          approved_by: userData.user.id,
          approved_at: new Date().toISOString(),
        })
        .eq("id", invitation.id);

      if (updateError) throw updateError;

      // Send the email invitation
      try {
        await supabase.functions.invoke("send-team-invite", {
          body: { invitation_id: invitation.id },
        });
      } catch (emailErr) {
        console.error("Failed to send email:", emailErr);
      }

      toast.success(`Invitation approved and sent to ${invitation.invitee_email}`);
      fetchInvitations();
      onUpdate();
    } catch (err: any) {
      console.error("Failed to approve invitation:", err);
      toast.error(err.message || "Failed to approve invitation");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (invitation: PendingInvitation) => {
    setProcessingId(invitation.id);

    try {
      const { error } = await supabase
        .from("team_member_invitations")
        .update({ status: "declined" })
        .eq("id", invitation.id);

      if (error) throw error;

      toast.success("Invitation rejected");
      fetchInvitations();
      onUpdate();
    } catch (err: any) {
      console.error("Failed to reject invitation:", err);
      toast.error(err.message || "Failed to reject invitation");
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return null;
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-600" />
          Pending Invitation Requests
          <Badge variant="secondary" className="ml-auto">
            {invitations.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {invitations.map((invitation) => (
          <div
            key={invitation.id}
            className="flex flex-col gap-2 p-3 bg-background rounded-lg border"
          >
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{invitation.invitee_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-3 w-3" />
                  {invitation.invitee_email}
                </div>
                {invitation.invitee_phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {invitation.invitee_phone}
                  </div>
                )}
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              Invited by {invitation.inviter_email} •{" "}
              {format(new Date(invitation.created_at), "MMM d, yyyy")}
            </div>

            <div className="flex gap-2 mt-1">
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => handleReject(invitation)}
                disabled={processingId === invitation.id}
              >
                {processingId === invitation.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </>
                )}
              </Button>
              <Button
                size="sm"
                className="flex-1"
                onClick={() => handleApprove(invitation)}
                disabled={processingId === invitation.id}
              >
                {processingId === invitation.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Approve & Send
                  </>
                )}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default PendingInvitationsCard;
